import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Link } from 'react-router-dom';
const NHLGameCard = ({ game, tierColor }) => {
    // Check if game is completed and has results
    const isGameFinal = game.result?.final || false;
    const hasUserBet = game.bet_outcome !== undefined;
    const calculateBetGrade = () => {
        // Prefer direct bet_grade from JSON (CBB, NBA) or betting_indicators (NHL)
        if (game.bet_grade)
            return game.bet_grade.toUpperCase();
        const actualGrade = game.betting_indicators?.bet_ranking?.grade;
        if (actualGrade) {
            return actualGrade;
        }
        // Fallback calculation based on confidence and probability
        const confidence = (game.ensemble_probability || 0) * 100;
        const edge = (game.betting_edge || 0) * 100;
        const confidenceLevel = getTrueConfidenceLevel();
        if (confidenceLevel === 'HIGH' && confidence >= 70 && edge >= 15)
            return 'A+';
        if (confidenceLevel === 'HIGH' && confidence >= 68)
            return 'A';
        if (confidenceLevel === 'HIGH')
            return 'A-';
        if (confidenceLevel === 'MEDIUM_PLUS' && confidence >= 65)
            return 'B+';
        if (confidenceLevel === 'MEDIUM_PLUS')
            return 'B';
        if (confidenceLevel === 'MEDIUM' && confidence >= 60)
            return 'B-';
        if (confidenceLevel === 'MEDIUM')
            return 'C+';
        if (confidenceLevel === 'LOW')
            return 'C';
        return 'C-';
    };
    const getBetGradeColor = (grade) => {
        if (!grade)
            return '#6c757d';
        const gradeUpper = grade.toUpperCase();
        if (gradeUpper.startsWith('A'))
            return '#198754';
        if (gradeUpper.startsWith('B'))
            return '#0dcaf0';
        if (gradeUpper.startsWith('C'))
            return '#ffc107';
        if (gradeUpper.startsWith('D') || gradeUpper.startsWith('F'))
            return '#dc3545';
        return '#6c757d';
    };
    const formatGameTime = (gameDate) => {
        try {
            const date = new Date(gameDate);
            if (isNaN(date.getTime())) {
                const parts = gameDate.split('-');
                if (parts.length === 3) {
                    const formattedDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                    return formattedDate.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                    });
                }
                return gameDate;
            }
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
            });
        }
        catch (e) {
            return gameDate;
        }
    };
    const getBetDisplayText = () => {
        const pickedTeam = game.team_to_cover;
        const isHomePick = pickedTeam === game.home_team;
        // Check if this is a spread sport (has spread_line) or moneyline sport
        const hasSpread = game.spread_line !== undefined && game.spread_line !== null;
        if (hasSpread) {
            // For spread sports (CBB, NBA), show spread bet
            const spread = game.spread_line || 0;
            const spreadText = spread > 0 ? `+${spread}` : `${spread}`;
            const spreadOdds = isHomePick ? game.home_spread_odds : game.away_spread_odds;
            if (spreadOdds === null || spreadOdds === undefined) {
                return `${pickedTeam} ${spreadText}`;
            }
            const oddsText = spreadOdds > 0 ? `+${spreadOdds}` : `${spreadOdds}`;
            return `${pickedTeam} ${spreadText} (${oddsText})`;
        }
        else {
            // For moneyline sports (NHL), show moneyline bet
            const odds = isHomePick ? game.home_ml : game.away_ml;
            // Handle null/missing odds
            if (odds === null || odds === undefined) {
                return `${pickedTeam} ML (No odds available)`;
            }
            const oddsText = odds > 0 ? `+${odds}` : `${odds}`;
            return `${pickedTeam} ML ${oddsText}`;
        }
    };
    const getTrueConfidenceLevel = () => {
        return game.confidence_level || 'LOW';
    };
    const getConfidenceColor = () => {
        const trueLevel = getTrueConfidenceLevel();
        switch (trueLevel) {
            case 'HIGH': return 'var(--bs-success)';
            case 'MEDIUM_PLUS': return 'var(--bs-primary)';
            case 'MEDIUM': return 'var(--bs-warning)';
            case 'LOW': return 'var(--bs-secondary)';
            default: return 'var(--bs-secondary)';
        }
    };
    const getPickProbability = () => {
        return Math.round(game.display_confidence || 0);
    };
    const getPickDescription = () => {
        return game.team_to_cover;
    };
    const getBettingOdds = () => {
        const pickedTeam = game.team_to_cover;
        const isHomePick = pickedTeam === game.home_team;
        // Check if this is a spread sport (has spread_line) or moneyline sport
        const hasSpread = game.spread_line !== undefined && game.spread_line !== null;
        if (hasSpread) {
            // For spread sports (CBB, NBA), show spread odds
            const spreadOdds = isHomePick ? game.home_spread_odds : game.away_spread_odds;
            if (spreadOdds === null || spreadOdds === undefined) {
                return 'N/A';
            }
            return spreadOdds > 0 ? `+${spreadOdds}` : `${spreadOdds}`;
        }
        else {
            // For moneyline sports (NHL), show moneyline odds
            const mlOdds = isHomePick ? game.home_ml : game.away_ml;
            if (mlOdds === null || mlOdds === undefined) {
                return 'N/A';
            }
            return mlOdds > 0 ? `+${mlOdds}` : `${mlOdds}`;
        }
    };
    // Bet outcome indicator
    const getBetOutcomeIndicator = () => {
        if (!hasUserBet || !isGameFinal)
            return null;
        const betWon = game.bet_outcome?.status === 'won';
        const icon = betWon ? '✓' : '✗';
        const color = betWon ? '#22c55e' : '#dc3545';
        return (_jsx("div", { style: {
                position: 'absolute',
                top: '8px',
                right: '8px',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: betWon ? 'rgba(34, 197, 94, 0.1)' : 'rgba(220, 53, 69, 0.1)',
                border: `2px solid ${color}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                fontWeight: 'bold',
                color: color,
                zIndex: 10
            }, title: betWon ? 'Bet Won!' : 'Bet Lost', children: icon }));
    };
    return (_jsx("div", { className: "card h-100", style: {
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
        }, onMouseEnter: (e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
        }, onMouseLeave: (e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
        }, children: _jsxs("div", { className: "card-body", style: { padding: '16px', position: 'relative' }, children: [getBetOutcomeIndicator(), isGameFinal && game.result ? (() => {
                    const winner = game.result.winner;
                    const predictedTeam = game.team_to_cover;
                    const predictedWinner = predictedTeam === game.home_team ? 'home' : 'away';
                    const wasCorrect = predictedWinner === winner;
                    const borderColor = wasCorrect ? '#22c55e' : '#dc3545';
                    const bgColor = wasCorrect ? 'rgba(34, 197, 94, 0.1)' : 'rgba(220, 53, 69, 0.1)';
                    const labelColor = wasCorrect ? '#22c55e' : '#dc3545';
                    const label = wasCorrect ? 'FINAL - CORRECT' : 'FINAL - INCORRECT';
                    return (_jsxs("div", { style: {
                            backgroundColor: bgColor,
                            border: `1px solid ${borderColor}`,
                            borderRadius: '6px',
                            padding: '12px',
                            textAlign: 'center',
                            marginBottom: '10px'
                        }, children: [_jsx("div", { style: { fontSize: '11px', color: labelColor, fontWeight: '600', marginBottom: '4px' }, children: label }), _jsxs("div", { style: { fontSize: '20px', fontWeight: '700', color: 'var(--bs-body-color)' }, children: [game.result.away_score, " - ", game.result.home_score] }), _jsxs("div", { style: { fontSize: '12px', color: '#6b7280', marginTop: '4px' }, children: ["Winner: ", game.result.winner === 'home' ? game.home_team : game.away_team] })] }));
                })() : null, !isGameFinal ? (_jsx("div", { className: "d-flex justify-content-between align-items-center mb-3", children: _jsxs("div", { className: "team-info", children: [_jsxs("h5", { className: "mb-0", style: { fontSize: '18px', fontWeight: '600', color: 'var(--bs-body-color)' }, children: [game.away_team, " @ ", game.home_team] }), _jsxs("div", { style: { fontSize: '12px', color: '#6b7280', marginTop: '4px' }, children: [formatGameTime(game.game_date), " \u2022 ", game.game_time] })] }) })) : null, _jsx("div", { className: "betting-line mb-3 bg-light", style: { borderRadius: '6px', padding: '12px' }, children: _jsxs("div", { className: "d-flex justify-content-between align-items-start", children: [_jsx("div", { className: "fw-semibold", style: { fontSize: '16px', color: 'var(--bs-body-color)' }, children: getBetDisplayText() }), _jsx("div", { className: "text-end", children: _jsxs("div", { className: "fw-bold", style: { fontSize: '14px', color: '#22c55e' }, children: ["Odds: ", getBettingOdds()] }) })] }) }), _jsxs("div", { className: "confidence-section mb-3", children: [_jsxs("div", { className: "d-flex justify-content-between align-items-center mb-2", children: [_jsx("span", { className: "text-muted", style: { fontSize: '12px' }, children: "Pick Confidence" }), _jsxs("span", { style: { fontSize: '12px', fontWeight: '500', color: 'var(--bs-body-color)' }, children: [getPickProbability(), "% ", getPickDescription()] })] }), _jsx("div", { className: "progress", style: { height: '10px', borderRadius: '5px' }, children: _jsx("div", { className: "progress-bar", style: {
                                    width: `${getPickProbability()}%`,
                                    backgroundColor: getConfidenceColor(),
                                    borderRadius: '5px'
                                } }) })] }), _jsx("div", { className: "criteria-status mb-3", children: _jsxs("div", { className: "row g-2", children: [_jsx("div", { className: "col-6", children: _jsxs("div", { className: "text-center p-2 bg-light", style: { borderRadius: '4px' }, children: [_jsx("div", { className: "text-secondary", style: { fontSize: '12px' }, children: "Confidence" }), _jsx("div", { style: { fontSize: '14px', fontWeight: '600', color: getConfidenceColor() }, children: getTrueConfidenceLevel() })] }) }), _jsx("div", { className: "col-6", children: _jsxs("div", { className: "text-center p-2 bg-light", style: { borderRadius: '4px' }, children: [_jsx("div", { className: "text-secondary", style: { fontSize: '12px' }, children: "Bet Grade" }), _jsx("div", { style: { fontSize: '14px', fontWeight: '600', color: getBetGradeColor(calculateBetGrade()) }, children: calculateBetGrade() })] }) })] }) }), game.individual_predictions && (_jsxs("div", { className: "model-agreement mb-3", children: [_jsx("div", { className: "text-muted", style: { fontSize: '11px', marginBottom: '6px' }, children: "Model Agreement" }), _jsx("div", { style: { fontSize: '12px' }, children: Object.entries(game.individual_predictions).map(([model, prediction]) => (_jsxs("div", { className: "d-flex justify-content-between", style: { marginBottom: '2px' }, children: [_jsx("span", { style: { textTransform: 'capitalize' }, children: model.replace('_', ' ') }), _jsx("span", { children: prediction })] }, model))) })] })), _jsxs(Link, { to: `/dashboard/nhl/${game.game_id}`, className: "btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center", style: {
                        fontSize: '12px',
                        fontWeight: '500',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        textDecoration: 'none',
                        minHeight: '44px'
                    }, children: [_jsx("i", { className: "fas fa-chart-line me-2" }), "View Analysis"] })] }) }));
};
export default NHLGameCard;
